import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { CheckCircle, Star } from "lucide-react";
import PremiumPricing from "./PremiumPricing";
import InteractiveCard from "./InteractiveCard";
import { useResponsive } from "../hooks/useResponsive";
import { colors } from "../styles/colors";
import { PrimaryButton, SecondaryButton } from "./ui/StandardButton";
import HeroHeading from "./ui/HeroHeading";
import packagesConfig from "../data/packagesConfig";
import SeoAlternateLinks from "./seo/SeoAlternateLinks";
import { buildAbsoluteUrl, derivePageMeta } from "../utils/seo";
import LazyVideo from "./LazyVideo";
import { useContent } from "../cms/ContentContext";

const Packages = () => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();
  const heroSpacing = isMobile ? "px-4 pt-24 pb-16" : "px-4 sm:px-6 lg:px-8 pt-32 pb-24";
  const meta = derivePageMeta({
    t,
    i18n,
    pageKey: "packages",
    routeKey: "packages",
    defaults: {
      ogImage: "/og/packages.jpg",
    },
  });
  const ogImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.ogImage);
  const twitterImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.twitterImage);
  
  // Memoize calculated values
  const calculatedValues = useMemo(() => {
    const cards = packagesConfig
      .map((pkg) => {
        const gradientValue =
          typeof pkg.gradient === "string" && colors.gradients[pkg.gradient]
            ? colors.gradients[pkg.gradient]
            : pkg.gradient;

        return {
          ...pkg,
          gradientValue,
        };
      })
      .sort((a, b) => {
        const orderA = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;

        if (orderA !== orderB) return orderA - orderB;
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return a.key.localeCompare(b.key);
      });

    const priceMap = cards.reduce((acc, pkg) => {
      if (typeof pkg.price === "number") {
        acc[pkg.key] = pkg.price;
      }
      return acc;
    }, {});

    return {
      langPrefix: `/${i18n.language}`,
      priceMap,
      cards,
    };
  }, [i18n.language]);
  return (
    <>
      <Helmet>
        {meta.title && <title>{meta.title}</title>}
        {meta.description && <meta name="description" content={meta.description} />}
        {meta.keywords && <meta name="keywords" content={meta.keywords} />}
        <link rel="canonical" href={meta.canonicalUrl} />
        <meta property="og:title" content={meta.ogTitle} />
        <meta property="og:description" content={meta.ogDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={meta.canonicalUrl} />
        <meta name="twitter:image" content={twitterImageUrl} />
        {meta.jsonLd && (
          <script type="application/ld+json">{JSON.stringify(meta.jsonLd)}</script>
        )}
      </Helmet>
      <SeoAlternateLinks
        routeKey="packages"
        canonicalPath={meta.canonicalPath}
        siteUrl={meta.siteUrl}
      />
      {/* Hero Section */}
      <section
        data-hero="true"
        data-cms-section="packages"
        data-cms-page="packages"
        id="packages-hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ background: colors.gradients.hero }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <LazyVideo
            src={getMedia('packages.hero.videoSrc', '/optimized_videos/coldexperience1-opt.mp4')}
            poster={getMedia('packages.hero.poster', '/images/Nya_bilder/IMG_6698.webp')}
            autoPlay
            muted
            loop
            playsInline
            preload={isMobile ? "none" : "metadata"}
            className="absolute inset-0"
            videoClassName="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: colors.gradients.hero }}></div>
        </div>

        <div className={`relative z-10 w-full ${heroSpacing}`}>
          <div className={`${isMobile ? "max-w-sm" : "max-w-4xl"} mx-auto text-center`} style={{ color: colors.text.primary }}>
            <HeroHeading
              title={t('packages.title')}
              stacked
              className={`mb-6 ${isMobile ? "text-4xl" : "text-5xl md:text-6xl"}`}
            />
            <p
              className={`${isMobile ? "text-lg" : "text-xl md:text-2xl"} mb-8 leading-relaxed`}
              style={{ color: colors.text.secondary }}
            >
              {t('packages.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryButton to={`${calculatedValues.langPrefix}/book`} size="large">
                {t('hero.cta', 'Book Adventure')}
              </PrimaryButton>
              <SecondaryButton to={`${calculatedValues.langPrefix}/contact`} size="large">
                {t('hero.explore', 'Contact Us')}
              </SecondaryButton>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section
        data-cms-section="packagesIntro"
        data-cms-page="packages"
        id="packages-intro"
        className={isMobile ? 'py-12' : 'py-20'}
        style={{ background: colors.gradients.heroSolid }}
      >
        <div className={`mx-auto ${
          isMobile ? 'max-w-sm px-4' : 'max-w-7xl px-4 sm:px-6 lg:px-8'
        }`}>
          {/* Section Header */}
          <div className={`text-center ${
            isMobile ? 'mb-8' : 'mb-16'
          }`}>
            <h2 
              className={`font-bold mb-4 ${
                isMobile ? 'text-3xl' : 'text-4xl'
              }`}
              style={{ color: colors.text.primary }}
            >
              {t('packages.title')}
            </h2>
            <p 
              className={`mx-auto ${
                isMobile ? 'text-lg max-w-md' : 'text-xl max-w-3xl'
              }`}
              style={{ color: colors.text.secondary }}
            >
              {t('packages.subtitle')}
            </p>
          </div>
          
          <div className={`grid gap-8 ${
            isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-4'
          }`}>
            {calculatedValues.cards.map((pkg) => (
              <InteractiveCard
                key={pkg.key}
                id={pkg.htmlId}
                data-cms-section={pkg.cmsSectionId}
                data-cms-page="packages"
                variant={pkg.key === "complete" ? "glass" : "tilt"}
                className={`relative overflow-hidden shadow-2xl ${
                  pkg.key === "complete" ? 'scale-105' : ''
                }`}
                style={{ 
                  backgroundColor: colors.secondary,
                  border: `1px solid ${colors.border}`,
                  ...(pkg.key === "complete" && { 
                    boxShadow: `0 0 0 2px ${colors.accent.primary}40`
                  })
                }}
              >
                {pkg.key === "complete" && (
                  <div className="absolute top-6 right-6 z-10">
                    <div 
                      className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                      style={{ 
                        backgroundColor: colors.accent.primary,
                        color: colors.text.primary 
                      }}
                    >
                      <Star size={14} className="fill-current" />
                      {t("packages.mostPopular")}
                    </div>
                  </div>
                )}
                
                {/* Package Image */}
                <div className={`relative overflow-hidden ${
                  isMobile ? 'h-48' : 'h-64'
                }`}>
                  <img
                    src={getMedia(pkg.imageKey, pkg.image)}
                    alt={t(`packages.${pkg.key}.name`)}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="absolute inset-0 opacity-40"
                    style={{ background: pkg.gradientValue }}
                  ></div>
                  <div className={`absolute ${
                    isMobile ? 'bottom-4 left-4' : 'bottom-6 left-6'
                  }`} style={{ color: colors.text.primary }}>
                    <h3 className={`font-bold mb-1 ${
                      isMobile ? 'text-xl' : 'text-2xl'
                    }`}>{t(`packages.${pkg.key}.name`)}</h3>
                    <p className={`opacity-90 font-medium ${
                      isMobile ? 'text-xs' : 'text-sm'
                    }`}>{t(`packages.${pkg.key}.duration`)}</p>
                  </div>
                </div>
                
                {/* Package Content */}
                <div className={isMobile ? 'p-6' : 'p-8'}>
                  <div className={`flex items-baseline justify-between ${
                    isMobile ? 'mb-4' : 'mb-6'
                  }`}>
                    <PremiumPricing 
                      price={pkg.price}
                      currency="EUR" 
                    />
                    <div 
                      className={`font-medium ${
                        isMobile ? 'text-xs' : 'text-sm'
                      }`}
                      style={{ color: colors.text.secondary }}
                    >
                      {t("packages.perPerson")}
                    </div>
                  </div>
                  
                  <p 
                    className={`leading-relaxed ${
                      isMobile ? 'mb-6 text-sm' : 'mb-8'
                    }`}
                    style={{ color: colors.text.primary }}
                  >
                    {t(`packages.${pkg.key}.description`)}
                  </p>
                  
                  <div className={`space-y-4 ${
                    isMobile ? 'mb-6' : 'mb-8'
                  }`}>
                    {(() => {
                      const highlights = t(`packages.${pkg.key}.highlights`, { returnObjects: true });
                      if (Array.isArray(highlights)) {
                        return highlights.map((highlight, highlightIndex) => (
                          <div key={highlightIndex} className="flex items-start space-x-3">
                            <CheckCircle 
                              size={18} 
                              className="flex-shrink-0 mt-0.5" 
                              style={{ color: colors.accent.primary }}
                            />
                            <span 
                              className="text-sm leading-relaxed"
                              style={{ color: colors.text.primary }}
                            >
                              {highlight}
                            </span>
                          </div>
                        ));
                      }
                      return null;
                    })()}
                  </div>
                  
                  {pkg.key === "complete" ? (
                    <PrimaryButton
                      to={`${calculatedValues.langPrefix}/book`}
                      className="w-full"
                      size="large"
                    >
                      {t("packages.bookButton")}
                    </PrimaryButton>
                  ) : (
                    <SecondaryButton
                      to={`${calculatedValues.langPrefix}/book`}
                      className="w-full"
                      size="large"
                    >
                      {t("packages.bookButton")}
                    </SecondaryButton>
                  )}
                </div>
              </InteractiveCard>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section
        data-cms-section="packagesCta"
        data-cms-page="packages"
        id="packages-cta"
        className={`relative text-center overflow-hidden ${isMobile ? 'py-12' : 'py-20'}`}
        style={{ backgroundColor: colors.secondary }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${getMedia('packages.cta.backgroundImage', '/images/Nya_bilder/IMG_4108.webp')})` }}
        />
        <div className={`relative z-10 mx-auto ${isMobile ? 'max-w-sm px-4' : 'max-w-3xl px-4'}`}>
          <h2
            className={`font-bold mb-6 ${isMobile ? 'text-2xl' : 'text-3xl'}`}
            style={{ color: colors.text.primary }}
          >
            {t('packages.title')}
          </h2>
          <p
            className={`mb-8 ${isMobile ? 'text-lg' : 'text-xl'}`}
            style={{ color: colors.text.secondary }}
          >
            {t('packages.subtitle')}
          </p>
          <PrimaryButton to={`${calculatedValues.langPrefix}/book`} size="large">
            {t('packages.bookButton')}
          </PrimaryButton>
        </div>
      </section>
      {/* Footer removed, now rendered globally in App.js */}
    </>
  );
};

export default Packages;
